import React, { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/Auth";
import { desktop, isDesktop } from "../lib/desktop";

/** App only: which website the app signs in to. */
function SiteAddress() {
  const [site, setSite] = useState("");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { desktop.status().then((s) => setSite(s.site_url)).catch(() => {}); }, []);
  const save = async () => { setError(""); try { await desktop.setSite(site); window.location.reload(); } catch (e) { setError(e.message); } };
  return (
    <div className="w-full max-w-sm mt-3 text-xs text-subtle">
      {editing ? (
        <div className="flex gap-2">
          <input className="control flex-1 mono text-xs" value={site} onChange={(e) => setSite(e.target.value)} aria-label="Website address" />
          <button type="button" className="button-secondary" onClick={save}>Connect</button>
        </div>
      ) : (
        <p>Website: <span className="mono">{site || "…"}</span> · <button type="button" className="text-accent hover:underline" onClick={() => setEditing(true)}>Change</button></p>
      )}
      {error && <p className="text-danger mt-1">{error}</p>}
    </div>
  );
}

// rivals-stats2.0's sign-in screen. Replace public/logo/login-background.png to change the left panel's art.
const INPUT = "bg-[#141414] border border-white/10 text-white px-4 py-3 mb-4 focus:border-[#27AE60] outline-none w-full";

export default function Login() {
  const { login, register } = useAuth();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.authStatus().then((s) => { setStatus(s); if (s.needs_setup) setMode("register"); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setup = status?.needs_setup;

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative items-end p-12 bg-cover bg-center"
        style={{ backgroundImage: "linear-gradient(to top, rgba(10,10,10,0.95), rgba(10,10,10,0.4)), url(/logo/login-background.png)" }}>
        <div>
          <div className="font-display text-7xl leading-none tracking-tight mb-3">
            WELCOME<br />TO<br />THE<br /><span className="text-[#27AE60]">SWAMP</span>
          </div>
          <p className="text-[#a0a0a0] max-w-sm">
            Skyza's Marvel Rival's Database <br />
            Prev Natus Vincere, Rad Esports EU
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-[#0a0a0a]">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#27AE60] flex items-center justify-center">
              <Shield className="w-6 h-6 text-black" />
            </div>
            <span className="font-display text-3xl tracking-tight">Rivals<span className="text-[#27AE60]">Stats</span></span>
          </div>
          <h1 className="font-display text-5xl tracking-tight mb-1">{mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}</h1>
          <p className="text-[#a0a0a0] mb-8 text-sm">{setup ? "First-time setup: this account becomes the site admin." : mode === "login" ? ":P." : "View-only until an admin promotes you."}</p>

          {error && (
            <div data-testid="login-error" className="mb-4 px-4 py-3 bg-[#ff2a2a]/10 border border-[#ff2a2a]/40 text-[#ff6b6b] text-sm">{error}</div>
          )}

          {mode === "register" && (
            <>
              <label className="label block mb-2">Display name</label>
              <input className={INPUT} value={form.name} onChange={update("name")} required autoComplete="name" />
            </>
          )}
          <label className="label block mb-2">Email</label>
          <input data-testid="login-email" type="email" className={INPUT} value={form.email} onChange={update("email")} required autoComplete="email" spellCheck="false" />
          <label className="label block mb-2">Password</label>
          <input data-testid="login-password" type="password" className={`${INPUT} !mb-6`} value={form.password} onChange={update("password")}
            required minLength={mode === "register" ? 8 : 1} autoComplete={mode === "login" ? "current-password" : "new-password"} />
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full bg-[#145A32] text-black font-bold uppercase tracking-widest py-3 hover:bg-[#27AE60]/80 transition-colors disabled:opacity-50">
            {busy ? (mode === "login" ? "Signing in..." : "Creating...") : mode === "login" ? "Sign In" : "Create Account"}
          </button>
          {!setup && status?.open_registration && (
            <button type="button" className="w-full mt-4 text-xs text-[#a0a0a0] hover:text-white" onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? "No account? Create one" : "Have an account? Sign in"}
            </button>
          )}
        </form>
        {isDesktop && <SiteAddress />}
      </div>
    </div>
  );
}
